import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import moment from 'moment';

import { Receptor } from '../../models/Catalogos';
import { CfdiIndexFilter, CfdiIndexStatus, CFDI_STATUS_LABEL, compactFilter } from '../../models/CfdiIndex';
import { graphqlSchema } from '../../services/graphql.schema';
import { clearGqlTypename } from '../../utils/formatting';
import AutoComplete from '../Forms/Autocomplete';
import Input from '../Forms/Input';
import Select from '../Forms/Select';
import DateRangeInput from '../Forms/DateRange';
import Button from '../Forms/Button';
import {
    applyTipoFilter, parseSearchText, searchTextFromFilter, STATUS_FILTER_OPTIONS,
    TIPO_FILTER_OPTIONS, tipoFilterValueFrom, TipoFilterValue
} from './facturas.actions';

// ─── Receptor autocomplete (shared with the reports view) ─────────────────────

interface ReceptorAutocompleteProps {
    rfc?: string;
    disabled?: boolean;
    label?: string;
    onChange: (rfc: string | null, receptor?: Receptor) => void;
}

export function ReceptorAutocomplete({ rfc, disabled, label, onChange }: ReceptorAutocompleteProps) {
    const [text, setText] = useState(rfc || '');
    const [options, setOptions] = useState<Receptor[]>([]);
    const debounceRef = useRef<number>(null);

    const [getByName] = useLazyQuery(graphqlSchema.PROFILE.CATALOGOS.getClienteByName, {
        onCompleted: ({ getClienteByName }: { getClienteByName: Receptor[] }) => {
            setOptions((getClienteByName || []).map(c => clearGqlTypename(c)));
        },
        onError: (e) => console.error('Error getting receptor by name: ', e),
    });

    useEffect(() => {
        // External reset (e.g. "Limpiar filtros")
        if (!rfc) setText('');
        else if (!text || (text !== rfc && !options.some(o => o.name === text))) setText(rfc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rfc]);

    const handleSearch = (value: string) => {
        setText(value);
        if (rfc) onChange(null);
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        if (!value) return;
        debounceRef.current = window.setTimeout(() => getByName({ variables: { name: value } }), 350);
    };

    const handleSelect = (receptorAsString: string) => {
        const receptor: Receptor = JSON.parse(receptorAsString);
        setText(receptor.name);
        onChange(receptor.rfc, receptor);
    };

    const receptorOptions = options.map(r => ({
        label: r.name,
        value: JSON.stringify(r),
        template: <div><p className="_big">{r.name}</p><p className="_small">{r.rfc}</p></div>
    }));

    return (
        <AutoComplete label={label || 'Cliente'} value={text} placeholder="Buscar por nombre o RFC" type="text"
            disabled={disabled} noChangeOnSelect={true} forcePropsValue={true} options={receptorOptions}
            onChange={handleSearch} onSelect={handleSelect} />
    );
}

// ─── Period selector ──────────────────────────────────────────────────────────

type PeriodValue = 'all' | 'thisMonth' | 'lastMonth' | 'month' | 'range';

const PERIOD_OPTIONS: { value: PeriodValue; label: string }[] = [
    { value: 'all', label: 'Cualquier fecha' },
    { value: 'thisMonth', label: 'Este mes' },
    { value: 'lastMonth', label: 'Mes anterior' },
    { value: 'month', label: 'Mes específico' },
    { value: 'range', label: 'Rango de fechas' },
];

const periodFromFilter = (filter: CfdiIndexFilter): PeriodValue => {
    if (filter.start === undefined && filter.end === undefined) return 'all';
    if (filter.start !== undefined && filter.end !== undefined) {
        const start = moment(filter.start);
        const end = moment(filter.end);
        const isWholeMonth = start.isSame(start.clone().startOf('month'), 'day') && end.isSame(start.clone().endOf('month'), 'day');
        if (isWholeMonth) {
            if (start.isSame(moment().startOf('month'), 'month')) return 'thisMonth';
            if (start.isSame(moment().subtract(1, 'month').startOf('month'), 'month')) return 'lastMonth';
            return 'month';
        }
    }
    return 'range';
};

const monthRange = (base: moment.Moment) => ({ start: base.clone().startOf('month').valueOf(), end: base.clone().endOf('month').valueOf() });

// ─── Filters bar ──────────────────────────────────────────────────────────────

interface FacturasFiltersProps {
    filter: CfdiIndexFilter;
    isLoading: boolean;
    onChange: (filter: CfdiIndexFilter) => void;
}

function FacturasFilters({ filter, isLoading, onChange }: FacturasFiltersProps) {
    const [searchText, setSearchText] = useState(searchTextFromFilter(filter));
    const [period, setPeriod] = useState<PeriodValue>(periodFromFilter(filter));
    const debounceRef = useRef<number>(null);

    useEffect(() => {
        // Sync from outside (URL navigation, clear). The period select is user-owned: only reset it when the
        // date bounds were removed externally, otherwise "Mes específico" would flip to "Este mes" on its own.
        const fromFilter = searchTextFromFilter(filter);
        if (!fromFilter && searchText) setSearchText('');
        const hasBounds = filter.start !== undefined || filter.end !== undefined;
        if (!hasBounds && period !== 'all') setPeriod('all');
        if (hasBounds && period === 'all') setPeriod(periodFromFilter(filter));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const emit = (next: CfdiIndexFilter) => onChange(compactFilter(next));

    const handleSearchText = (value: string) => {
        setSearchText(value);
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
            const rest: CfdiIndexFilter = { ...filter };
            delete rest.uuid; delete rest.serie; delete rest.folio; delete rest.nombre;
            emit({ ...rest, ...parseSearchText(value) });
        }, 350);
    };

    const handleReceptor = (rfc: string | null) => {
        const rest: CfdiIndexFilter = { ...filter };
        delete rest.rfc;
        emit(rfc ? { ...rest, rfc } : rest);
    };

    const handlePeriod = (value: PeriodValue) => {
        setPeriod(value);
        const { start, end } = filter;
        const rest: CfdiIndexFilter = { ...filter };
        delete rest.start; delete rest.end;
        if (value === 'all') return emit(rest);
        if (value === 'thisMonth') return emit({ ...rest, ...monthRange(moment()) });
        if (value === 'lastMonth') return emit({ ...rest, ...monthRange(moment().subtract(1, 'month')) });
        if (value === 'month') return emit({ ...rest, ...monthRange(start ? moment(start) : moment()) });
        // range: keep current bounds (if any) until the user picks dates
        if (start !== undefined) rest.start = start;
        if (end !== undefined) rest.end = end;
        emit(rest);
    };

    const handleMonth = (date: Date | string) => {
        if (!date) return;
        emit({ ...filter, ...monthRange(moment(date)) });
    };

    const handleRange = (start: Date, end: Date) => {
        const next: CfdiIndexFilter = { ...filter };
        if (start) next.start = moment(start).startOf('day').valueOf(); else delete next.start;
        if (end) next.end = moment(end).endOf('day').valueOf(); else delete next.end;
        emit(next);
    };

    const handleStatus = (value: string) => {
        const rest: CfdiIndexFilter = { ...filter };
        delete rest.status;
        emit(value ? { ...rest, status: value as CfdiIndexStatus } : rest);
    };

    const handleTipo = (value: TipoFilterValue) => emit(applyTipoFilter(filter, value));

    const clearAll = () => {
        setSearchText('');
        setPeriod('all');
        emit({});
    };

    const monthValue = filter.start ? moment(filter.start).startOf('month').valueOf() : moment().startOf('month').valueOf();
    // When a date control is visible (month / range) the date chip is redundant with it
    const showsDateControl = period === 'month' || period === 'range';
    const chips = describeFilter(showsDateControl ? { ...filter, start: undefined, end: undefined } : filter);
    const hasActiveFilters = Object.keys(compactFilter(filter)).length > 0;

    return (
        <div className="facturasFilters">
            <div className="filtersRow">
                <Input type="text" label="Buscar" value={searchText} placeholder="UUID, Serie-Folio (A-123), Folio o nombre del cliente"
                    disabled={isLoading} onChange={handleSearchText} />
                <ReceptorAutocomplete rfc={filter.rfc} disabled={isLoading} onChange={handleReceptor} />
                <Select label="Estado" options={STATUS_FILTER_OPTIONS} value={filter.status || ''} disabled={isLoading} onChange={handleStatus} />
                <Select label="Tipo de comprobante" options={TIPO_FILTER_OPTIONS} value={tipoFilterValueFrom(filter)} disabled={isLoading} onChange={handleTipo} />
            </div>
            <div className="filtersRow period">
                <Select label="Periodo" options={PERIOD_OPTIONS} value={period} disabled={isLoading} onChange={handlePeriod} />
                {
                    period === 'month' ?
                        <Input type="date" datepickerFormat="months" dateFormat="MMMM yyyy" label="Mes" value={monthValue} placeholder="Selecciona el mes"
                            maxDate={moment().endOf('month').toDate()} disabled={isLoading} onChange={handleMonth} />
                        : null
                }
                {
                    period === 'range' ?
                        <DateRangeInput label="Inicio / Fin" startDate={filter.start ? new Date(filter.start) : null} endDate={filter.end ? new Date(filter.end) : null}
                            disabled={isLoading} onChange={handleRange} />
                        : null
                }
                <div className="chips">
                    {chips.map(chip => <span className="chip" key={chip}>{chip}</span>)}
                    {
                        hasActiveFilters ?
                            <Button tertiary={true} size="small" handleClick={clearAll}>
                                <span>Limpiar filtros</span>
                            </Button>
                            : null
                    }
                </div>
            </div>
        </div>
    );
}

/** Human readable summary of active filters (chips) */
export const describeFilter = (filter: CfdiIndexFilter): string[] => {
    const chips: string[] = [];
    if (filter.start !== undefined || filter.end !== undefined) {
        const start = filter.start !== undefined ? moment(filter.start).format('DD MMM YYYY') : '…';
        const end = filter.end !== undefined ? moment(filter.end).format('DD MMM YYYY') : '…';
        chips.push(`${start} → ${end}`);
    }
    if (filter.rfc) chips.push(`Cliente: ${filter.rfc}`);
    if (filter.nombre) chips.push(`Nombre o Serie: ${filter.nombre}`);
    if (filter.uuid) chips.push(`UUID: ${filter.uuid}`);
    if (filter.serie && filter.folio) chips.push(`Serie-Folio: ${filter.serie}-${filter.folio}`);
    else if (filter.folio) chips.push(`Folio: ${filter.folio}`);
    else if (filter.serie) chips.push(`Serie: ${filter.serie}`);
    if (filter.status) chips.push(`Estado: ${CFDI_STATUS_LABEL[filter.status]}`);
    const tipo = tipoFilterValueFrom(filter);
    if (tipo !== 'ALL') chips.push(TIPO_FILTER_OPTIONS.find(o => o.value === tipo)?.label || tipo);
    return chips;
};

export default FacturasFilters;
